import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * member-profile-images는 비공개 버킷이라 public URL을 쓸 수 없다.
 * signed URL을 메모리·sessionStorage에 캐시해 재발급 빈도를 줄인다.
 */
const BUCKET = 'member-profile-images';
const SIGNED_URL_TTL_SECONDS = 3600;
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const SS_KEY = 'cp.memberPhotoSignedUrl.v1';

type CacheEntry = {
  url: string;
  expiresAt: number;
  inflight?: Promise<string | null>;
};

type SsMap = Record<string, { url: string; expiresAt: number }>;

const cache = new Map<string, CacheEntry>();

function cacheKey(path: string, version?: number): string {
  return version ? `${path}::v${version}` : path;
}

function readSsMap(): SsMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(SS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as SsMap;
  } catch {
    return {};
  }
}

function writeSsEntry(key: string, url: string, expiresAt: number): void {
  if (typeof window === 'undefined') return;
  try {
    const map = readSsMap();
    map[key] = { url, expiresAt };
    const now = Date.now();
    for (const k of Object.keys(map)) {
      if ((map[k]?.expiresAt ?? 0) <= now) delete map[k];
    }
    window.sessionStorage.setItem(SS_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

function peekSs(key: string): CacheEntry | null {
  const row = readSsMap()[key];
  if (!row?.url || !row.expiresAt) return null;
  if (row.expiresAt - REFRESH_MARGIN_MS <= Date.now()) return null;
  return { url: row.url, expiresAt: row.expiresAt };
}

async function fetchSignedUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

/** 동기 peek — 메모리/세션에 유효 signed URL이 있으면 즉시 */
export function peekMemberPhotoSignedUrl(
  path?: string | null,
  version?: number
): string | null {
  const trimmed = (path ?? '').trim();
  if (!trimmed) return null;
  const key = cacheKey(trimmed, version);
  const now = Date.now();
  const existing = cache.get(key);
  if (existing?.url && existing.expiresAt - REFRESH_MARGIN_MS > now && !existing.inflight) {
    return existing.url;
  }
  const ss = peekSs(key);
  if (ss) {
    cache.set(key, ss);
    return ss.url;
  }
  return null;
}

/** 멤버 실사 프로필 사진의 signed URL을 반환(캐시 히트 시 즉시, 만료 근접 시 재발급) */
export async function getMemberPhotoSignedUrl(
  path?: string | null,
  version?: number
): Promise<string | null> {
  const trimmed = (path ?? '').trim();
  if (!trimmed) return null;
  const key = cacheKey(trimmed, version);
  const now = Date.now();
  const existing = cache.get(key);
  if (existing) {
    if (existing.inflight) return existing.inflight;
    if (existing.expiresAt - REFRESH_MARGIN_MS > now) return existing.url;
  } else {
    const ss = peekSs(key);
    if (ss) {
      cache.set(key, ss);
      return ss.url;
    }
  }

  const inflight = fetchSignedUrl(trimmed).then((url) => {
    if (url) {
      const expiresAt = Date.now() + SIGNED_URL_TTL_SECONDS * 1000;
      cache.set(key, { url, expiresAt });
      writeSsEntry(key, url, expiresAt);
    } else {
      cache.delete(key);
    }
    return url;
  });
  cache.set(key, { url: existing?.url ?? '', expiresAt: existing?.expiresAt ?? 0, inflight });
  return inflight;
}

/**
 * @deprecated 표시용은 `useMemberPhotoDisplayUrl`(Blob 캐시)를 쓰세요.
 * 호환용 — signed URL peek + 비동기 조회.
 */
export function useMemberPhotoSignedUrl(
  path?: string | null,
  version?: number
): string | null {
  const trimmed = (path ?? '').trim();
  const [url, setUrl] = useState<string | null>(() =>
    trimmed ? peekMemberPhotoSignedUrl(trimmed, version) : null
  );

  useEffect(() => {
    if (!trimmed) {
      setUrl(null);
      return;
    }
    const peeked = peekMemberPhotoSignedUrl(trimmed, version);
    if (peeked) setUrl(peeked);
    else setUrl(null);
    let cancelled = false;
    void getMemberPhotoSignedUrl(trimmed, version).then((next) => {
      if (!cancelled && next) setUrl(next);
    });
    return () => {
      cancelled = true;
    };
  }, [trimmed, version]);

  return url;
}
